<script lang="ts">
	import { Config } from '#lib/constants/config.js';
	import { NewsletterDialog } from '#lib/features/newsletter/components/NewsletterDialog/index.js';
	import { NewsletterProvenance } from '#lib/features/newsletter/constants/mixpanel-events.js';
	import {
		ArticleSearchCategory,
		BASE_SEARCH_CATEGORIES_MAP,
	} from '#lib/websites/news/constants/categories.js';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { Button } from '$global/components/Button';
	import { Container } from '$global/components/Container';
	import { createDialogStores } from '$global/components/Dialog';
	import { Txt } from '$global/components/Txt';

	interface Props {
		phrase: string | null;
		geotag: string | null;
		artist: string | null;
	}

	let { phrase, geotag, artist }: Props = $props();

	let searchParams = $derived(page.url.searchParams);

	let term = $derived(phrase || geotag || artist);

	let categoryParam = $derived(searchParams.get('category'));

	const newsletterDialogStores = createDialogStores();

	const handleClickSignUp = () => {
		newsletterDialogStores.states.open.set(true);
	};

	const handleClickCategory = (category?: string) => () => {
		// `page.url.searchParams` is readonly in SvelteKit 3: edit a copy.
		const params = new URLSearchParams(searchParams.toString());

		if (category) {
			params.set('category', category);
		} else {
			params.delete('category');
		}

		goto(`?${params.toString()}`, { reset: false });
	};

	const CATEGORY_BUTTONS = [
		{
			label: 'Top',
			value: BASE_SEARCH_CATEGORIES_MAP[ArticleSearchCategory.shoutouts],
		},
		{
			label: 'Market',
			value: BASE_SEARCH_CATEGORIES_MAP[ArticleSearchCategory.market],
		},
		{
			label: 'Shows',
			value: BASE_SEARCH_CATEGORIES_MAP[ArticleSearchCategory.shows],
		},
		{
			label: 'Profile',
			value: BASE_SEARCH_CATEGORIES_MAP[ArticleSearchCategory.people],
		},
		{
			label: 'Legal',
			value: BASE_SEARCH_CATEGORIES_MAP[ArticleSearchCategory.legal],
		},
		{
			label: 'Museum',
			value: BASE_SEARCH_CATEGORIES_MAP[ArticleSearchCategory.museum],
		},
	];
</script>

<Container dataCy="news-home" class="bg-gray-100">
	<div class="mb-4 flex items-center justify-between pt-12 lg:pt-16">
		<Txt variant="h4">
			{phrase ? `News feed for "${phrase}"` : `${term} news feed`}
		</Txt>

		<Button
			dataCy="news-home-page-sign-up"
			variant="secondary"
			size="md"
			onclick={handleClickSignUp}
			class="hidden lg:flex"
		>
			Join the HENI Art Newsletter

			{#snippet trailing()}
				<ChevronRightIcon />
			{/snippet}
		</Button>
	</div>

	<div class="flex flex-nowrap gap-2 overflow-x-auto py-2">
		<Button
			dataCy="news-home-page-category-button"
			variant={!categoryParam ? 'primary' : 'secondary'}
			size="sm"
			class=""
			onclick={handleClickCategory()}
		>
			All News
		</Button>

		{#each CATEGORY_BUTTONS as button (button.value)}
			<Button
				dataCy="news-home-page-category-button"
				variant={categoryParam?.toLowerCase() === button.value.toLowerCase()
					? 'primary'
					: 'secondary'}
				size="sm"
				class=""
				onclick={handleClickCategory(button.value)}
			>
				{button.label}
			</Button>
		{/each}
	</div>
</Container>

<NewsletterDialog
	dataCy="news-home-page"
	listId={Config.NewsletterListIds.News}
	title="Get the latest updates"
	provenance={NewsletterProvenance.NewsHome}
	description="Sign up for the HENI Newsletter to receive updates on future releases,
		collaborations and events."
	dialogStores={newsletterDialogStores}
/>
